import React, { useState } from "react";
import { Link } from "react-router-dom";
import { walkiesClient } from "../api/walkiesClient";

type SubmitState = "idle" | "submitting" | "submitted" | "error";

const OwnerConversationAccessPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setSubmitState("error");
      setSubmitError("Please enter your email address.");
      return;
    }

    try {
      setSubmitState("submitting");
      setSubmitError("");
      setSubmitSuccess("");

      const result = await walkiesClient.requestOwnerConversationAccess({
        email: trimmedEmail,
        maxConversations: 10,
      });

      setSubmitState("submitted");
      setSubmitSuccess(
        result.message ||
          "If we found any conversations for that email address, we’ve sent secure access links to your inbox."
      );
    } catch (err) {
      setSubmitState("error");
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Failed to send secure access links."
      );
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <section style={styles.heroCard}>
          <div style={styles.badgeRow}>
            <span style={styles.badge}>Owner access</span>
          </div>

          <h1 style={styles.title}>Get back into your conversations</h1>
          <p style={styles.subtitle}>
            Enter the email address you used when you contacted a walker. If we
            find matching conversations, we’ll send secure access links to your
            inbox.
          </p>
        </section>

        <section style={styles.formCard}>
          {submitState === "submitted" && submitSuccess && (
            <div style={styles.successBox}>{submitSuccess}</div>
          )}

          {submitState === "error" && submitError && (
            <div style={styles.errorBox}>{submitError}</div>
          )}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.field}>
              <label htmlFor="ownerEmail" style={styles.label}>
                Your email address
              </label>
              <input
                id="ownerEmail"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);

                  if (submitState !== "idle") {
                    setSubmitState("idle");
                    setSubmitError("");
                    setSubmitSuccess("");
                  }
                }}
                placeholder="you@example.com"
                style={styles.input}
                required
              />
            </div>

            <div style={styles.formActions}>
              <button
                type="submit"
                style={styles.primaryButton}
                disabled={submitState === "submitting"}
              >
                {submitState === "submitting"
                  ? "Sending links..."
                  : "Email me secure access links"}
              </button>

              <Link to="/" style={styles.secondaryLinkButton}>
                Back to home
              </Link>
            </div>
          </form>

          <div style={styles.infoBox}>
            This does not create an account. It simply emails you secure links to
            any recent WalkiesNearMe conversations we find for that email address.
          </div>
        </section>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top right, rgba(242, 138, 22, 0.16), transparent 28%), linear-gradient(180deg, #2f4f46 0%, #213a33 100%)",
    padding: "32px 16px 64px",
    color: "#ffffff",
  },
  container: {
    maxWidth: 920,
    margin: "0 auto",
  },
  heroCard: {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 28,
    padding: 24,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
    marginBottom: 22,
  },
  badgeRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 13,
    fontWeight: 800,
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.12)",
  },
  title: {
    margin: "0 0 10px",
    fontSize: 40,
    lineHeight: 1.02,
    letterSpacing: "-0.04em",
    fontWeight: 900,
  },
  subtitle: {
    margin: 0,
    fontSize: 16,
    lineHeight: 1.7,
    color: "rgba(255,255,255,0.82)",
  },
  formCard: {
    background: "#f5f0e8",
    color: "#18201d",
    borderRadius: 28,
    padding: 24,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
  },
  form: {
    display: "grid",
    gap: 16,
  },
  field: {
    display: "grid",
    gap: 8,
  },
  label: {
    fontWeight: 800,
    color: "#213a33",
  },
  input: {
    width: "100%",
    font: "inherit",
    padding: "14px 14px",
    borderRadius: 14,
    outline: "none",
    border: "1px solid rgba(24, 32, 29, 0.14)",
    background: "#ffffff",
    color: "#18201d",
  },
  formActions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: 0,
    borderRadius: 16,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 800,
    cursor: "pointer",
    background: "#f28a16",
    color: "#ffffff",
  },
  secondaryLinkButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 800,
    background: "#213a33",
    color: "#ffffff",
    textDecoration: "none",
  },
  successBox: {
    borderRadius: 16,
    padding: 14,
    background: "rgba(33, 58, 51, 0.08)",
    border: "1px solid rgba(33, 58, 51, 0.12)",
    color: "#213a33",
    fontWeight: 700,
    marginBottom: 16,
    lineHeight: 1.7,
  },
  errorBox: {
    borderRadius: 16,
    padding: 14,
    background: "rgba(220, 38, 38, 0.08)",
    border: "1px solid rgba(220, 38, 38, 0.14)",
    color: "#7f1d1d",
    fontWeight: 700,
    marginBottom: 16,
    lineHeight: 1.7,
  },
  infoBox: {
    marginTop: 18,
    borderRadius: 18,
    padding: 16,
    background: "rgba(24, 32, 29, 0.05)",
    color: "rgba(24, 32, 29, 0.78)",
    lineHeight: 1.7,
  },
};

export default OwnerConversationAccessPage;